import {
  Permissions,
  WILDCARD,
  grantCovers,
  type MyRoleGrant,
} from '@/models/permission';

export interface ManagementScope {
  /** Coverage over every Sandbox: the Game-wide Permission Manager. */
  gameWide: boolean;
  /** The Sandboxes covered, empty when gameWide. */
  sandboxCodes: string[];
}

/**
 * Which Sandboxes of one Game the viewer administers permissions on — the scope
 * banner's only input. Derived from the Grants carrying `admin:manage_permissions`,
 * never from a Role name (#419 AC 11).
 *
 * Whether the viewer may act on a *given* scope is not asked here: that is a point
 * check, answered by `usePermissionCheck` / `hasPermission` with the target's
 * game_code and sandbox_code. This function exists for the one question a point
 * check cannot answer — enumerating the scopes held.
 */
export const managementScope = (
  grants: MyRoleGrant[],
  gameCode: string,
): ManagementScope => {
  // Sandbox left unconstrained: managing any Sandbox of the Game opens the Access
  // area; the Sandbox restriction applies to what may be edited, not to visibility.
  const managing = grants.filter((grant) =>
    grantCovers(grant, Permissions.admin.managePermissions, gameCode),
  );
  const gameWide = managing.some(
    ({ sandbox_code }) => sandbox_code === WILDCARD,
  );

  return {
    gameWide,
    sandboxCodes: gameWide
      ? []
      : [...new Set(managing.map(({ sandbox_code }) => sandbox_code))].sort(),
  };
};

/** "All Sandboxes" / "QA Sandbox only" / "QA, DEV Sandboxes only". */
export const managementScopeLabel = ({
  gameWide,
  sandboxCodes,
}: ManagementScope) => {
  if (gameWide) return 'All Sandboxes';
  if (sandboxCodes.length === 1) return `${sandboxCodes[0]} Sandbox only`;
  return `${sandboxCodes.join(', ')} Sandboxes only`;
};

/** The viewer's own Grants carrying `admin:manage_permissions`, on any scope. */
export const managementGrants = (grants: MyRoleGrant[]) =>
  grants.filter((grant) =>
    grantCovers(grant, Permissions.admin.managePermissions),
  );

/**
 * Whether revoking `grant` would strip the viewer of their last
 * permission-management Grant (#419 AC 9).
 *
 * The join is role name + scope — unique per Grant, and the only key available:
 * `RoleGrantResponse` carries no role id. Which Grants count as
 * permission-management is derived from the permissions they carry, never from the
 * Role name (AC 11); the name only ever reaches the copy. admin-api enforces the
 * same rule and stays authoritative (srvc/permission/grant_service.go:111).
 */
export const isLastManagementGrant = (
  myGrants: MyRoleGrant[],
  grant: MyRoleGrant,
) => {
  const managing = managementGrants(myGrants);
  const [last] = managing;

  return (
    managing.length === 1 &&
    last?.role === grant.role &&
    last.game_code === grant.game_code &&
    last.sandbox_code === grant.sandbox_code
  );
};
